import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";

import EditActionbar from "core/blueprint/competencelist/EditActionbar";
import {
	ReportFieldRights,
	ReportRight,
	ReportRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { Field } from "core/field/FieldService";

import Prompt from "lib/components/prompt/Prompt";
import useHistory from "lib/history/useHistory";

import { AddAction, ReplaceAction } from "utils/history/PatchAction";
import { asTitle } from "utils/Utils";

import {
	getTrajectoryReport,
	ReportValues,
	updateTrajectoryReport
} from "./ReportService";

import IconButton from "@mui/material/IconButton";
import { getTrajectoryReports, ReportInfo } from "trajectory/TrajectoryService";
import ReportFilter from "./ReportFilter";
import ReportForm from "./ReportForm";
import ReportViewActionbar from "./ReportViewActionbar";
import SplitscreenDialog from "./SplitScreenDialog";

export type ReportViewProps = {
	trajectoryId: number;
	reportId: number;
	rights: ReportRights;
	labels: Map<string, Array<string>>;
};

const ReportView: React.FC<ReportViewProps> = (props: ReportViewProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const location = useLocation();

	const [reports, setReports] = useState<ReportInfo[]>([]);
	const [reportStructure, setReportStructure] = useState<Field[]>([]);

	const [reportId, setReportId] = useState<number>(props.reportId);

	const [error, setError] = useState<string | undefined>(undefined);
	const [success, setSuccess] = useState<string | undefined>(undefined);

	const history = useHistory<ReportValues>({ initialState: {} });

	const [edit, setEdit] = useState(searchParams.get("edit") === "true");

	const [splitScreen, setSplitScreen] = useState(false);
	const [splitReportId, setSplitReportId] = useState<number>();
	const [splitStructure, setSplitStructure] = useState<Field[]>();
	const [splitValues, setSplitValues] = useState<ReportValues>();
	const hasSplitScreen =
		splitReportId !== undefined && splitStructure && splitValues;

	const [filter, setFilter] = useState<Array<string>>([]);

	const labels = useMemo(() => {
		const result = new Set<string>();
		props.labels.forEach(value => {
			value.forEach(label => result.add(label));
		});

		return [...result];
	}, [props.labels]);

	useEffect(() => {
		loadReport();
	}, [props.trajectoryId, reportId]);

	useEffect(() => {
		loadReports();
	}, [props.trajectoryId]);

	function loadReports(): Promise<void> {
		return getTrajectoryReports(props.trajectoryId).then(data =>
			setReports(data)
		);
	}

	function loadReport() {
		getTrajectoryReport(props.trajectoryId, reportId)
			.then(data => {
				setReportStructure(data.structure);
				history.reset(data.values);
			})
			.catch(() =>
				setError(t("loadDocumentError", { document: "report" }))
			);
	}

	function loadSplitReport(reportId: number): Promise<void> {
		return getTrajectoryReport(props.trajectoryId, reportId)
			.then(data => {
				setSplitReportId(reportId);
				setSplitStructure(data.structure);
				setSplitValues(data.values);
			})
			.catch(() =>
				setError(t("loadDocumentError", { document: "report" }))
			);
	}

	function saveReport(): Promise<void> {
		const patches = history.actions.flatMap(action => action.patches());

		return updateTrajectoryReport(props.trajectoryId, reportId, patches)
			.then(loadReport)
			.then(() => setSuccess(t("saveReportSuccess")))
			.then(() => setEdit(false))
			.catch(() =>
				setError(t("saveDocumentFailure", { document: "report" }))
			);
	}

	function handleChange(uuid: string, newValue: string): void {
		const oldValue = history.state[uuid];
		if (oldValue === undefined) {
			const action = new AddAction("/" + uuid, newValue);
			history.perform(action);
		} else {
			const action = new ReplaceAction("/" + uuid, oldValue, newValue);
			history.perform(action);
		}
	}

	// A report's id is its index in the trajectory's full list, while the
	// list served here omits the report types this user may not read. So look
	// a report up by id, and only use positions to walk the visible list.
	const position = reports.findIndex(report => report.id === reportId);

	function getReportName(id: number): string {
		const report = reports.find(r => r.id === id);
		if (!report) {
			return "";
		}

		return `${format(parseISO(report.created), "dd/MM/yyyy HH:mm:ss")} (${
			report.name
		})`;
	}

	// TrajectoryView passes empty rights until the trajectory's access and
	// report blueprints have loaded, which can be after these reports have.
	// Until then a report has no rights.
	function getFieldRights(id: number | undefined): ReportFieldRights {
		const reportType = reports.find(report => report.id === id)?.name;
		if (reportType === undefined) {
			return {};
		}

		return props.rights[reportType] ?? {};
	}

	function getReportRight(right: ReportRight): boolean {
		const fieldRights = getFieldRights(reportId);
		if (fieldRights[""] === undefined) {
			return false;
		}

		if (right === ReportRight.ADMIN) {
			return fieldRights[""][right];
		}

		if (fieldRights[""][right]) {
			return true;
		} else {
			return Object.entries(fieldRights)
				.filter(entry => entry[0] !== "")
				.map(entry => entry[1])
				.some(rights => rights[right - 1]);
		}
	}

	function handleSplitScreenClick(): void {
		setSplitScreen(!splitScreen);
		if (splitScreen) {
			setSplitReportId(undefined);
			setSplitStructure(undefined);
			setSplitValues(undefined);
		}
	}

	const handleClose = useCallback(() => {
		const path = location.pathname;
		navigate(path.substring(0, path.lastIndexOf("/")));
	}, [location.pathname, navigate]);

	const handleUndo = useCallback(() => {
		history.undo();
	}, [history]);

	const handleRedo = useCallback(() => {
		history.redo();
	}, [history]);

	const goToPrevious = useCallback(() => {
		if (history.hasPrevious && !window.confirm(t("exitDocumentMessage"))) {
			return;
		}

		if (position > 0) {
			setReportId(reports[position - 1].id);
		}
	}, [history.hasPrevious, reports, position]);

	const goToNext = useCallback(() => {
		if (history.hasPrevious && !window.confirm(t("exitDocumentMessage"))) {
			return;
		}

		if (position >= 0 && position < reports.length - 1) {
			setReportId(reports[position + 1].id);
		}
	}, [history.hasPrevious, reports, position]);

	return (
		<>
			<Box sx={{ displayPrint: "none" }}>
				{error && (
					<Alert id="error-message" severity="error">
						{error}
					</Alert>
				)}
				{success && (
					<Alert id="success-message" severity="success">
						{success}
					</Alert>
				)}

				<Grid
					container
					alignItems={"center"}
					justifyContent="space-between">
					<Grid item>
						{edit ? (
							<EditActionbar
								undoEnabled={history.hasPrevious}
								redoEnabled={history.hasNext}
								saveEnabled={history.hasPrevious}
								onUndo={handleUndo}
								onRedo={handleRedo}
								onSave={saveReport}
								onClose={handleClose}
							/>
						) : (
							<ReportViewActionbar
								editEnabled={getReportRight(ReportRight.WRITE)}
								onEdit={() => setEdit(true)}
								onClose={handleClose}
							/>
						)}
					</Grid>

					<Grid item>
						<Tooltip title={asTitle(t("previous"))}>
							<span>
								<IconButton
									disabled={position <= 0}
									onClick={goToPrevious}>
									<ArrowBackIcon />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title={asTitle(t("next"))}>
							<span>
								<IconButton
									disabled={
										position < 0 ||
										position === reports.length - 1
									}
									onClick={goToNext}>
									<ArrowForwardIcon />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title={asTitle(t("split screen"))}>
							<span>
								<ToggleButton
									value="splitScreen"
									color="primary"
									selected={splitScreen}
									onChange={handleSplitScreenClick}>
									<SplitscreenIcon
										sx={{ transform: "rotate(90deg)" }}
									/>
								</ToggleButton>
							</span>
						</Tooltip>
					</Grid>
				</Grid>

				<ReportFilter labels={labels} onUpdate={setFilter} />
			</Box>

			<Grid container columnSpacing={2}>
				<Grid
					item
					xs={hasSplitScreen ? 6 : 12}
					sx={
						hasSplitScreen
							? { height: "100vh", overflowY: "scroll" }
							: {}
					}>
					<>
						<Typography variant="h6">
							{getReportName(reportId)}
						</Typography>
						<ReportForm
							structure={reportStructure}
							values={history.state}
							labels={props.labels}
							filter={filter}
							rights={getFieldRights(reportId)}
							edit={edit && getReportRight(ReportRight.WRITE)}
							onChange={handleChange}
						/>
					</>
				</Grid>
				<Grid
					item
					xs={hasSplitScreen ? 6 : 0}
					sx={
						hasSplitScreen
							? { height: "100vh", overflowY: "scroll" }
							: {}
					}>
					{hasSplitScreen && (
						<>
							<Typography variant="h6">
								{getReportName(splitReportId)}
							</Typography>
							<ReportForm
								structure={splitStructure}
								values={splitValues}
								labels={props.labels}
								filter={filter}
								rights={getFieldRights(splitReportId)}
								edit={false}
								onChange={() => {}}
							/>
						</>
					)}
				</Grid>
			</Grid>

			{splitScreen && !hasSplitScreen && (
				<SplitscreenDialog
					reports={reports}
					onSelect={loadSplitReport}
					onClose={() => setSplitScreen(false)}
				/>
			)}

			{edit && (
				<Prompt
					when={history.hasPrevious}
					message={t("exitDocumentMessage")}
				/>
			)}
		</>
	);
};

export default ReportView;
